import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DarkNavSurface, DarkNavPanel } from "@/components/nav/dark-nav-surface";

export const Route = createFileRoute("/dev/ui-preview")({
  component: UiPreview,
  head: () => ({
    meta: [
      { title: "UI Preview — LexAIPro" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

type Theme = "light" | "dark";

function UiPreview() {
  const [theme, setTheme] = useState<Theme>("light");

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    return () => root.classList.remove("dark");
  }, [theme]);

  const variants = [
    "default",
    "destructive",
    "outline",
    "secondary",
    "ghost",
    "link",
    "on-dark",
    "on-dark-ghost",
    "on-dark-outline",
  ] as const;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-border bg-background/90 px-6 py-3 backdrop-blur">
        <h1 className="text-lg font-semibold">UI Preview</h1>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Tema</span>
          <Button
            size="sm"
            variant={theme === "light" ? "default" : "outline"}
            onClick={() => setTheme("light")}
          >
            Light
          </Button>
          <Button
            size="sm"
            variant={theme === "dark" ? "default" : "outline"}
            onClick={() => setTheme("dark")}
          >
            Dark
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-10 p-6">
        <section>
          <h2 className="mb-4 text-xl font-semibold">Bottoni — tutte le varianti & stati</h2>
          <p className="mb-6 text-sm text-muted-foreground">
            Riga 1: stato normale · Riga 2: hover simulato · Riga 3: focus-visible (Tab per
            verificare anello) · Riga 4: disabilitato.
          </p>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-muted text-muted-foreground">
                <tr>
                  <th className="p-3 text-left">Variant</th>
                  <th className="p-3 text-left">Default</th>
                  <th className="p-3 text-left">Hover (sim.)</th>
                  <th className="p-3 text-left">Focus-visible</th>
                  <th className="p-3 text-left">Disabled</th>
                </tr>
              </thead>
              <tbody>
                {variants.map((v) => {
                  const isOnDark = v.startsWith("on-dark");
                  const cellCls = isOnDark ? "p-3 bg-black" : "p-3";
                  return (
                    <tr key={v} className="border-t border-border">
                      <td className="p-3 font-mono text-xs">{v}</td>
                      <td className={cellCls}>
                        <Button variant={v}>Azione</Button>
                      </td>
                      <td className={cellCls}>
                        <Button variant={v} data-hover className="hover-sim">
                          Azione
                        </Button>
                      </td>
                      <td className={cellCls}>
                        <Button variant={v} autoFocus={false} data-testid={`focus-${v}`}>
                          Azione
                        </Button>
                      </td>
                      <td className={cellCls}>
                        <Button variant={v} disabled>
                          Azione
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="mb-4 text-xl font-semibold">Navbar scura (header + drawer)</h2>
          <DarkNavSurface className="rounded-xl px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-white">LexAIPro</span>
              <div className="flex items-center gap-2">
                <Button asChild variant="on-dark-ghost" size="pill">
                  <a href="#">Accedi</a>
                </Button>
                <Button asChild variant="on-dark" size="pill">
                  <a href="#">Inizia gratis</a>
                </Button>
              </div>
            </div>
          </DarkNavSurface>
          <DarkNavPanel className="mt-4 rounded-2xl p-4">
            <p className="text-sm text-white/85">Drawer mobile — link e CTA leggibili</p>
            <div className="mt-3 flex gap-2">
              <Button asChild variant="on-dark-outline" size="pill-lg">
                <a href="#">Accedi</a>
              </Button>
              <Button asChild variant="on-dark" size="pill-lg">
                <a href="#">Inizia gratis</a>
              </Button>
            </div>
          </DarkNavPanel>
        </section>

        <section>
          <h2 className="mb-4 text-xl font-semibold">Tipografia su sfondi semantici</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {[
              ["bg-background text-foreground border-border", "Background"],
              ["bg-card text-card-foreground border-border", "Card"],
              ["bg-muted text-muted-foreground border-border", "Muted"],
              ["bg-primary text-primary-foreground border-transparent", "Primary"],
              ["bg-secondary text-secondary-foreground border-transparent", "Secondary"],
              ["bg-destructive text-destructive-foreground border-transparent", "Destructive"],
            ].map(([cls, label]) => (
              <div key={label} className={`rounded-lg border p-4 ${cls}`}>
                <p className="text-sm font-semibold">{label}</p>
                <p className="text-sm opacity-90">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                </p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <style>{`.hover-sim { background-color: var(--hover-bg); }`}</style>
    </div>
  );
}
